import React, {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useMatch } from "react-router-dom";
import "./ProductSingle.css";
import Header from "./common/Header";
import { Link } from "react-router-dom";
import httpService from "../services/HttpService"; // Import your HttpService (adjust path as needed)
import ImageSwiper from "./common/ImageSwiper";
import { formattedPrice } from "../utils/priceFormatter";
import SizeChartModal from "./common/SizeChartModal";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import { Footer } from "./common/Footer";

// Выносим компонент изображений в отдельный компонент
const ProductImages = React.memo(
  ({ images, mainImageIndex, onImageClick, toggleSave, product }) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const containerRef = useRef(null);
    const [clickedImageIndex, setClickedImageIndex] = useState(0);
    const [zoomedFullScreen, setZoomedFullscreen] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const currentImage = useRef(null);

    const handleImageClick = (e) => {
      currentImage.current = e.target;
      if (!zoomedFullScreen) {
        setZoomedFullscreen(true);
      } else {
        setZoomedFullscreen(false);
      }
    };

    const handleThumbnailClick = (index) => {
      setClickedImageIndex(index);
      setIsFullScreen(true);
      setZoomedFullscreen(false);
    };

    const handleFullScreenClose = () => {
      setIsFullScreen(false);
      setZoomedFullscreen(false);
    };

    useEffect(() => {
      if (isFullScreen && containerRef.current) {
        document.body.style.overflow = "hidden";
        const imageElement = containerRef.current.children[clickedImageIndex];
        if (imageElement) {
          imageElement.scrollIntoView();
        }
      } else {
        document.body.style.overflow = "unset";
      }
    }, [isFullScreen, clickedImageIndex]);
    useEffect(() => {
      if (currentImage.current) {
        currentImage.current.scrollIntoView();
      }
    }, [zoomedFullScreen]);

    return (
      <div className="product-single-images">
        <div style={{ width: "100%", display: "flex" }}>
          {images?.length > 1 && (
            <div className="product-single-image-list">
              {images.map((image, index) => (
                <img
                  fetchpriority="high"
                  key={index}
                  src={image.link}
                  alt={`Thumbnail ${index + 1}`}
                  onClick={() => onImageClick(index)}
                  style={{ cursor: "pointer" }}
                  className={index === mainImageIndex ? "active-thumbnail" : ""}
                  loading="eager"
                />
              ))}
            </div>
          )}
          <div className="product-single-right-image">
            <img
              fetchpriority="high"
              src={images[mainImageIndex]?.link}
              alt="Main img"
              loading="eager"
              onClick={() => handleThumbnailClick(mainImageIndex)}
              style={{ cursor: "zoom-in" }}
            />
            <button
              className="product-list-favorite-icon"
              onClick={() => toggleSave(product)}
            >
              <span
                className="material-symbols-sharp"
                style={{
                  fontVariationSettings: `'FILL' ${
                    product.favourite?.id ? 1 : 0
                  }, 'wght' 400, 'GRAD' 0, 'opsz' 24`,
                  transition: "font-variation-settings 0.3s ease-in-out",
                  cursor: "pointer",
                }}
              >
                bookmark
              </span>
            </button>
          </div>
        </div>

        {isFullScreen && (
          <div className="fullscreen-image-modal modal-overflow">
            <button
              className="fullscreen-close-btn"
              onClick={handleFullScreenClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="product-image-icon-close"
                viewBox="0 0 14 14"
                width="20"
                height="20"
                aria-hidden="true"
                focusable="false"
                role="presentation"
                fill="none"
              >
                <path
                  d="M.693 1.4l11.749 11.749.707-.707L1.4.693z"
                  fill="currentColor"
                ></path>
                <path
                  d="M12.442.693L.693 12.442l.707.707L13.149 1.4z"
                  fill="currentColor"
                ></path>
              </svg>
            </button>
            <div
              className="fullscreen-images-container"
              onClick={handleImageClick}
              ref={containerRef}
            >
              {images.map((image, index) => (
                <img
                  key={index}
                  src={image.link}
                  alt={`Full screen ${index + 1}`}
                  className="fullscreen-image"
                  style={{
                    height: zoomedFullScreen ? "auto" : "100vh",
                    cursor: zoomedFullScreen ? "zoom-out" : "zoom-in",
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

// Выносим компонент рекомендаций
const ProductRecommendations = React.memo(
  ({ recommendations, onProductClick }) => {
    return (
      <section className="recommended-products-section">
        {recommendations.map((recommendation, index) => (
          <div className="recommended-product" key={index}>
            <Link
              className="link"
              to={`/product/${recommendation.id}`}
              onClick={() => onProductClick(0)}
            >
              <div className="recommended-product-image">
                <img
                  src={recommendation.main_image.link}
                  alt={recommendation.name}
                  loading="lazy"
                />
              </div>
            </Link>
            <div className="recommended-product-info">
              <h4>{recommendation.brand.name}</h4>
              <p>{recommendation.name}</p>
              <p>
                {formattedPrice(
                  recommendation.price,
                  recommendation.actual_price,
                  recommendation.sale
                )}
              </p>
            </div>
          </div>
        ))}
      </section>
    );
  }
);

function ProductSinglePage() {
  const match = useMatch("/product/:productId");
  const productId = match?.params?.productId;
  const effectRan = useRef(false);
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [open, setOpen] = useState(null);
  const [alertedStocks, setAlertedStocks] = useState(new Set());
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsNext, setRecommendationsNext] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restocking, setRestocking] = useState({});
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const [sizeChart, setSizeChart] = useState(null);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const headerRef = useRef();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  useEffect(() => {
    const sendStatistics = async () => {
      try {
        const blogger = queryParams.get("b");

        if (blogger) {
          await httpService.post("/campaign", {
            name: "product-" + productId + "-b-" + blogger,
          });
        }
      } catch (error) {
        console.error("Ошибка при отправке статистики:", error);
      }
    };

    sendStatistics();
  }, [productId]);

  useEffect(() => {
    const onPopState = (e) => {
      // Пользователь нажал "назад"
      navigate("/product", { replace: true });
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [navigate]);

  useLayoutEffect(() => {
    setLoading(true);
    setProduct(null);
    fetchProduct().then(() => {
      setLoading(false);
    });
    fetchRecommendations();
    fetchRecommendationsNext();
  }, [productId]);

  const fetchSizeChart = async (product) => {
    try {
      const response = await httpService.get(
        `/size-chart?category_id=${product.category_id}&brand_id=${product.brand_id}&product_id=${product.id}`
      );
      const res = await response.json();
      setSizeChart(res.table);
    } catch (error) {
      setSizeChart(null);
      console.error("Failed to fetch size chart:", error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await httpService
        .get(`/products/${productId}`)
        .then((response) => response.json());
      setProduct(response);
      setAlertedItems();
      fetchSizeChart(response);
      return response;
    } catch (error) {
      console.error("Error fetching product:", error);
      setError(error.message);
      return null;
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await httpService
        .get(`/products/${productId}/same-category`)
        .then((response) => response.json());
      setRecommendations(response);
      return response;
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      return null;
    }
  };
  const fetchRecommendationsNext = async () => {
    try {
      const response = await httpService
        .get(`/products/${productId}/recommendations`)
        .then((response) => response.json());
      setRecommendationsNext(response);
      return response;
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      return null;
    }
  };

  const setAlertedItems = () => {
    if (!product) return;
    const initialAlerted = new Set(
      product.stocks.filter((s) => s.alert_count > 0).map((s) => s.id)
    );
    setAlertedStocks(initialAlerted);
  };

  const handleImageClick = useCallback((index) => {
    setMainImageIndex(index);
  }, []);

  const handleSizeSelect = useCallback((size) => {
    setSelectedSize(size);
    handleAddToCart(size);
  }, []);

  const toggleRestock = async (stockId, size) => {
    try {
      const isAlerted = alertedStocks.has(stockId);
      const endpoint = "/restock-alerts";
      const method = isAlerted ? "delete" : "post";
      const payload = { product_id: productId, size };

      const response = await httpService[method](endpoint, payload);
      const res = await response.json();

      if ((isAlerted && res?.message) || (!isAlerted && res?.id)) {
        setAlertedStocks((prev) => {
          const updated = new Set(prev);
          isAlerted ? updated.delete(stockId) : updated.add(stockId);
          return updated;
        });

        setNotification(
          isAlerted
            ? "Оповещение удалено"
            : "Оповещение о пополнении отправлено"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      setNotification("Ошибка");
    }

    setTimeout(() => setNotification(null), 1500);
  };

  const handleAddToCart = async (size) => {
    if (!size) {
      setNotification("Пожалуйста, выберите размер");
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    const cartData = {
      product_id: productId,
      size,
      count: 1,
    };

    try {
      const response = await httpService.post("/cart", cartData);

      const data = await response.json();

      if (response.ok) {
        if (data.id) {

          window.ym && window.ym(101728239, 'reachGoal', 'add_to_cart');

          headerRef.current?.incrementCart();
          setNotification("Успешно добавлено");
          setSelectedSize(null);
        }
      } else if (response.status === 422) {
        const errorMessage = response.message || "Ошибка";
        setNotification(errorMessage);
      } else {
        setNotification("Ошибка при добавлении в корзину");
      }
    } catch (error) {
      console.error("Error:", error);
      setNotification("Ошибка при добавлении в корзину");
    }

    // Clear the notification after a few seconds
    setTimeout(() => setNotification(null), 1500);
  };

  if (loading) {
    return (
      <div>
        <Header ref={headerRef} />
        <main className="product-main">
          <div className="zametna-loader-centered">zametna</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header ref={headerRef} />
        <main className="product-main">
          <div>Error: {error}</div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div>
        <Header ref={headerRef} />
        <main className="product-main">
          <div>Product not found</div>
        </main>
      </div>
    );
  }
  const toggleSave = async (product) => {
    try {
      if (product.favourite?.id) {
        const favouriteId = product.favourite?.id;
        const response = await httpService.delete(`/favourites/${favouriteId}`);
        const res = await response.json();

        if (!res.message) throw new Error("Failed to remove favorite");

        setProduct((prev) => ({
          ...prev,
          favourite: null,
        }));
      } else {
        const response = await httpService.post("/favourites", {
          product_id: product.id,
        });
        const res = await response.json();

        if (res?.id) {
          setProduct((prev) => ({
            ...prev,
            favourite: { id: res.id },
          }));
        } else {
          throw new Error("Invalid response from server");
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };
  const handleClickTableSizes = () => {
    setIsSizeChartOpen(true);
  };

  return (
    <div>
      <Header ref={headerRef} />
      <main className="product-main">
        <section className="product-single-section">
          {!isMobile ? (
            <ProductImages
              images={product?.images || []}
              mainImageIndex={mainImageIndex}
              onImageClick={handleImageClick}
              toggleSave={toggleSave}
              product={product}
            />
          ) : (
            <ImageSwiper
              images={(product?.images || []).map((img) => img.link)}
              toggleSave={toggleSave}
              product={product}
            />
          )}
          <div className="product-single-info-box">
            <div className="product-single-info">
              {product?.brand && (
                <Link className="link" to={`/brand/${product.brand.id}`}>
                  <h4>{product.brand.name}</h4>
                </Link>
              )}
              <p className="product-name">{product?.name || ""}</p>
              <p className="price">
                {formattedPrice(
                  product.price,
                  product.actual_price,
                  product.sale
                )}
              </p>
            </div>
            <div className="size-selector-container">
              <div className="chart-section">
                <span className="chart-section-size-text">Выберите размер</span>
                {sizeChart && (
                  <button
                    className="product-chart-btn"
                    onClick={handleClickTableSizes}
                  >
                    <span className="material-symbols-sharp">straighten</span>{" "}
                    Таблица размеров
                  </button>
                )}
              </div>
              <div className="size-list">
                {product.stocks.map((stock, index) => (
                  <button
                    key={index}
                    style={{
                      background: stock.count === 0 ? "#ececec" : "white",
                      border: stock.count === 0 ? "none" : "",
                    }}
                    className={`size-button ${
                      selectedSize === stock.size ? "active" : ""
                    }`}
                    onClick={() =>
                      stock.count === 0
                        ? toggleRestock(stock.id, stock.size)
                        : setSelectedSize(stock.size)
                    }
                  >
                    {stock.size}

                    {stock.count === 1 && (
                      <span className="size-quantity">{stock.count} шт.</span>
                    )}

                    {stock.count === 0 && (
                      <span
                        className={`material-symbols-sharp alert-icon ${
                          alertedStocks.has(stock.id) ? "filled" : "outlined"
                        }`}
                      >
                        {alertedStocks.has(stock.id)
                          ? "notifications_none"
                          : "notifications"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            {dropdownOpen && (
              <div
                className="bottom-sheet-overlay"
                onClick={() => setDropdownOpen(false)}
              ></div>
            )}

            <button
              className="add-button"
              onClick={() => handleAddToCart(selectedSize)}
            >
              Добавить в корзину
            </button>
          </div>
        </section>
        <div className={isSizeChartOpen ? "chart-modal-overlay" : ""} onClick={()=> setIsSizeChartOpen(false)}></div>
        {isSizeChartOpen && (
          <SizeChartModal
            onSizeSelect={handleSizeSelect}
            tableData={sizeChart}
            closeChartModal={() => setIsSizeChartOpen(false)}
          />
        )}
        {notification && <div className="notification">{notification}</div>}
        <div className="product-details">
          <div
            className="section"
            onClick={() => setOpen(open === 1 ? null : 1)}
          >
            <div>
              <span className="product-single-section-title">Описание</span>
              {open === 1 && (
                <div className="product-single-section-description">
                  {product.description}
                </div>
              )}
            </div>
            <span className={`chevron ${open === 1 ? "rotated" : ""}`}>
              <span className="material-symbols-sharp">add</span>
            </span>
          </div>
          <div
            className="section"
            onClick={() => setOpen(open === 2 ? null : 2)}
          >
            <div>
              <span className="product-single-section-title">Состав</span>
              {open === 2 && (
                <div className="product-single-section-description">
                  <div>{product.material}</div>
                </div>
              )}
            </div>
            <span className={`chevron ${open === 2 ? "rotated" : ""}`}>
              <span className="material-symbols-sharp">add</span>
            </span>
          </div>
          <div
            className="section"
            onClick={() => setOpen(open === 3 ? null : 3)}
          >
            <div>
              <span className="product-single-section-title">
                Рекомендации по уходу
              </span>
              {open === 3 && (
                <div className="product-single-section-description">
                  {product.care_guide}
                </div>
              )}
            </div>
            <span className={`chevron ${open === 3 ? "rotated" : ""}`}>
              <span className="material-symbols-sharp">add</span>
            </span>
          </div>

          <div
            className="section"
            onClick={() => setOpen(open === 4 ? null : 4)}
          >
            <div>
              <span className="product-single-section-title">
                Доставка и оплата
              </span>
              {open === 4 && (
                <div className="product-single-section-description">
                  Доставка с примеркой: выберите удобное для вас время при
                  оформлении заказа
                  <br />
                  <br />
                  Стоимость доставки:
                  <br />
                  - бесплатно – при стоимости покупки свыше 30 000 руб.
                  <br />
                  - 700 руб. при стоимости покупки менее 30 000 руб.
                  <br />
                  <br />
                  Оплата доступна как при получении так и онлайн
                </div>
              )}
            </div>
            <span className={`chevron ${open === 4 ? "rotated" : ""}`}>
              <span className="material-symbols-sharp">add</span>
            </span>
          </div>
        </div>

        <h3 className="recommendations-title">Посмотрите также</h3>
        <ProductRecommendations
          recommendations={recommendations}
          onProductClick={handleImageClick}
        />
        <h3 className="recommendations-title">Вам может понравиться</h3>
        <ProductRecommendations
          recommendations={recommendationsNext}
          onProductClick={handleImageClick}
        />
        {/* Bottom Sheet */}
        <div className={`bottom-sheet ${dropdownOpen ? "open" : ""}`}>
          <div className="bottom-sheet-header">
            <span>Выберите размер</span>
            <button onClick={() => setDropdownOpen(false)}>Закрыть</button>
          </div>
          {product.stocks.map((stock, index) => (
            <div
              key={index}
              className={`size-option ${
                selectedSize === stock.size ? "selected" : ""
              }`}
              onClick={() => handleSizeSelect(stock.size)}
            >
              {stock.size} — {stock.count} шт.
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default React.memo(ProductSinglePage);
